import { FiArrowUpCircle, FiLock } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { cn } from "utils/helpers";

import { useGuestActionBlock } from "../../hooks/useGuestActionBlock";

/**
 * FE-4 upgrade prompt. Rendered alongside a DISABLED mutating control whenever
 * the viewer is a guest — the control is never hidden, so the guest can see the
 * capability exists and is told exactly how to unlock it. Copy is sourced from
 * the guest-block strings (mirroring `rbac.guest_upgrade_required`).
 */

interface GuestUpgradeCtaProps {
	/** Invoked when the guest starts the company-upgrade flow (VT-027). */
	onUpgrade?: () => void;
	className?: string;
	testId?: string;
}

export const GuestUpgradeCta = ({
	onUpgrade,
	className,
	testId = "guest-upgrade-cta",
}: GuestUpgradeCtaProps) => {
	const { message, ctaLabel, viewOnlyLabel } = useGuestActionBlock();

	return (
		<div
			className={cn(
				"flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 sm:flex-row sm:items-center sm:justify-between",
				className
			)}
			data-testid={testId}
			role="note"
		>
			<div className="flex items-start gap-2 text-sm text-blue-800">
				<FiLock size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
				<div>
					<span
						className="mr-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
						data-testid="guest-view-only-badge"
					>
						{viewOnlyLabel}
					</span>
					<span>{message}</span>
				</div>
			</div>
			{onUpgrade && (
				<Button
					btntype="primary"
					size="sm"
					onClick={onUpgrade}
					data-testid="guest-upgrade-button"
				>
					<span className="pr-2">
						<FiArrowUpCircle size={16} aria-hidden="true" />
					</span>
					{ctaLabel}
				</Button>
			)}
		</div>
	);
};
